import React from "react";
import clsx from "clsx";

import { format, isAfter } from "date-fns";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import type {
    TMonthsBasedDatePickerDataDuration,
    TMonthsBasedDatePickerDataDate,
    TMonthsBasedDatePickerDataRange,
    TMonthsBasedDatePickerItem,
    TMonthsBasedDatePickerOption,
    TMonthsBasedDatePickerDataManuallyTypedDate,
} from "@/ui/molecules/MonthsBasedDatePicker";
import MonthsBasedDatePicker, {
    isDataDurationTypeGuard,
    isDataRangeTypeGuard,
    isDataDateTypeGuard,
    isDataManuallyTypedTypeGuard,
    generateMonthsOptionsWithQuartersAsFloatingItems,
    calculateDateFromDuration,
    parseDateIfItsValidAndMatchesFormat,
} from "@/ui/molecules/MonthsBasedDatePicker";

import { addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth } from "./addOneMonthToDateAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth";

export type TDateRange = {
    startDate: string;
    endDate: string;
};

export type TEndDateItem = TMonthsBasedDatePickerItem<
    | TMonthsBasedDatePickerDataDate
    | TMonthsBasedDatePickerDataRange
    | TMonthsBasedDatePickerDataManuallyTypedDate
>;

export type TStartDateItem = TMonthsBasedDatePickerItem<
    | TMonthsBasedDatePickerDataDuration
    | TMonthsBasedDatePickerDataDate
    | TMonthsBasedDatePickerDataManuallyTypedDate
>;

export type MonthBasedRangePickerProps = Omit<
    BoxProps,
    "onChange" | "onError"
> & {
    currentDate: string;
    lastEndDate?: string;
    highlightedEndDates?: string[];
    disabled?: boolean;
    onChange: (value: TDateRange) => void;
    onError: (message: string) => void;
    transformRangeToOutputFormat?: (value: TDateRange) => TDateRange;
    showOutputMonth?: boolean;
    showOutputYear?: boolean;
    /**
     * When this value changes (after mount), the "Output month" picker will be
     * reset back to `currentDate` and `onChange` will be fired accordingly.
     */
    resetOutputMonthKey?: string | number;
};

const getEndDateString = (item: TEndDateItem): string => {
    const data = item.data;
    if (isDataRangeTypeGuard(data)) {
        return data.end;
    } else {
        return data.value;
    }
};

const getStartDateString = (
    item: TStartDateItem,
    currentDate: string,
): string => {
    const data = item.data;
    if (isDataDurationTypeGuard(data)) {
        return calculateDateFromDuration({
            referenceDate: currentDate,
            formatString: "MM/yyyy",
            duration: data.value,
        });
    } else {
        return data.value;
    }
};

const getYearFromMonthYearString = (dateString: string): number => {
    const parsedOrFalse = parseDateIfItsValidAndMatchesFormat({
        dateString,
        formatString: "MM/yyyy",
    });
    if (parsedOrFalse === false) {
        throw new Error("Invalid month/year date string");
    }
    return Number(format(parsedOrFalse, "yyyy"));
};

const oneMonthOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { months: -1 },
                ),
            },
            label: "1 Month",
            id: "1-month",
        },
    };

const threeMonthsOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { months: -3 },
                ),
            },
            label: "3 Months",
            id: "3-month",
        },
    };

const sixMonthsOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { months: -6 },
                ),
            },
            label: "6 Months",
            id: "6-month",
        },
    };

const nineMonthsOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { months: -9 },
                ),
            },
            label: "9 Months",
            id: "9-month",
        },
    };

const oneYearOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { years: -1 },
                ),
            },
            label: "1 Year",
            id: "1-year",
        },
    };

const threeYearsOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { years: -3 },
                ),
            },
            label: "3 Years",
            id: "3-year",
        },
    };

const fiveYearsOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDuration> =
    {
        item: {
            data: {
                type: "duration",
                value: addOneMonthToDurationAsStartDateIsFirstDayOfTheMonthAndEndDateIsLastDayOfTheMonth(
                    { years: -5 },
                ),
            },
            label: "5 Years",
            id: "5-year",
        },
    };

const noLimitOption: TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDate> =
    {
        item: {
            data: { type: "date", value: "01/1970" },
            label: "No limit",
            id: "no-limit",
        },
    };

const startDateOptions: TMonthsBasedDatePickerOption<
    TMonthsBasedDatePickerDataDuration | TMonthsBasedDatePickerDataDate
>[] = [
    oneMonthOption,
    threeMonthsOption,
    sixMonthsOption,
    nineMonthsOption,
    oneYearOption,
    threeYearsOption,
    fiveYearsOption,
    noLimitOption,
];

export const testIds = {
    main: "MonthBasedRangePicker",
    startDate: "MonthBasedRangePicker-startDate",
    endDate: "MonthBasedRangePicker-endDate",
};

const MonthBasedRangePicker: React.FC<MonthBasedRangePickerProps> = ({
    sx,
    className,
    currentDate,
    lastEndDate,
    highlightedEndDates = [],
    disabled = false,
    onChange,
    onError,
    transformRangeToOutputFormat = value => value,
    showOutputMonth = true,
    showOutputYear = false,
    resetOutputMonthKey,
    ...otherProps
}) => {
    const [key, setKey] = React.useState(0);
    const forceReset = React.useCallback(() => setKey(prev => prev + 1), []);

    const [startDateSelectedItem, setStartDateSelectedItem] =
        React.useState<TStartDateItem>(oneYearOption.item);
    const [endDateSelectedItem, setEndDateSelectedItem] =
        React.useState<TEndDateItem>({
            data: { type: "date", value: currentDate },
            label: currentDate,
            id: currentDate,
        });
    const startDateSelectedItemRef = React.useRef<TStartDateItem>(
        startDateSelectedItem,
    );
    const onChangeRef = React.useRef(onChange);
    const transformRangeToOutputFormatRef = React.useRef(
        transformRangeToOutputFormat,
    );
    React.useEffect(() => {
        startDateSelectedItemRef.current = startDateSelectedItem;
    }, [startDateSelectedItem]);
    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    React.useEffect(() => {
        transformRangeToOutputFormatRef.current = transformRangeToOutputFormat;
    }, [transformRangeToOutputFormat]);
    const notifyAboutDefaultRange = React.useCallback(() => {
        onChange(
            transformRangeToOutputFormat({
                startDate: getStartDateString(
                    startDateSelectedItem,
                    currentDate,
                ),
                endDate: getEndDateString(endDateSelectedItem),
            }),
        );
    }, [
        onChange,
        transformRangeToOutputFormat,
        startDateSelectedItem,
        currentDate,
        endDateSelectedItem,
    ]);
    // mount only use effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(notifyAboutDefaultRange, []);

    const didMountRef = React.useRef(false);
    React.useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        const newEndDateSelectedItem: TEndDateItem = {
            data: { type: "date", value: currentDate },
            label: currentDate,
            id: currentDate,
        };
        setEndDateSelectedItem(newEndDateSelectedItem);

        const startDateString = getStartDateString(
            startDateSelectedItemRef.current,
            currentDate,
        );
        const endDateString = getEndDateString(newEndDateSelectedItem);
        onChangeRef.current(
            transformRangeToOutputFormatRef.current({
                startDate: startDateString,
                endDate: endDateString,
            }),
        );
    }, [resetOutputMonthKey, currentDate]);

    const endDateOptions = React.useMemo(() => {
        const currentDateMinusTwoYears = calculateDateFromDuration({
            referenceDate: currentDate,
            formatString: "MM/yyyy",
            duration: { years: -2 },
        });
        return generateMonthsOptionsWithQuartersAsFloatingItems(
            currentDate,
            lastEndDate ?? currentDateMinusTwoYears,
            highlightedEndDates,
        );
    }, [currentDate, lastEndDate, highlightedEndDates]);

    const yearOptions = React.useMemo(() => {
        const currentYear = getYearFromMonthYearString(currentDate);
        const minimumYear =
            lastEndDate !== undefined
                ? getYearFromMonthYearString(lastEndDate)
                : currentYear - 2;

        const optionsCount = Math.max(0, currentYear - minimumYear + 1);
        return Array.from({ length: optionsCount }, (_, i) => {
            const year = currentYear - i;
            return {
                item: {
                    data: { type: "date" as const, value: `${year}` },
                    label: `${year}`,
                    id: `${year}`,
                },
            } satisfies TMonthsBasedDatePickerOption<TMonthsBasedDatePickerDataDate>;
        });
    }, [currentDate, lastEndDate]);

    const handleStartDateChange = (newStartDateItem: TStartDateItem) => {
        const currentEndDateString = getEndDateString(endDateSelectedItem);
        const newStartDateString = getStartDateString(
            newStartDateItem,
            currentEndDateString,
        );

        const newStartDate = parseDateIfItsValidAndMatchesFormat({
            dateString: newStartDateString,
            formatString: "MM/yyyy",
        });
        if (newStartDate === false) return;

        const currentEndDate = parseDateIfItsValidAndMatchesFormat({
            dateString: currentEndDateString,
            formatString: "MM/yyyy",
        });
        if (currentEndDate === false) return;

        if (isAfter(newStartDate, currentEndDate)) {
            onError("Start date cannot be after end date");
            forceReset();
            return;
        }

        setStartDateSelectedItem(newStartDateItem);
        onChange(
            transformRangeToOutputFormat({
                startDate: newStartDateString,
                endDate: currentEndDateString,
            }),
        );
    };

    const handleEndDateChange = (newEndDateItem: TEndDateItem) => {
        if (isDataRangeTypeGuard(newEndDateItem.data)) {
            setEndDateSelectedItem(newEndDateItem);
            setStartDateSelectedItem({
                data: { type: "date", value: newEndDateItem.data.start },
                label: newEndDateItem.data.start,
                id: newEndDateItem.data.start,
            });
            onChange(
                transformRangeToOutputFormat({
                    startDate: newEndDateItem.data.start,
                    endDate: newEndDateItem.data.end,
                }),
            );
            return;
        }
        const newEndDateString = getEndDateString(newEndDateItem);
        const newEndDate = parseDateIfItsValidAndMatchesFormat({
            dateString: newEndDateString,
            formatString: "MM/yyyy",
        });
        if (newEndDate === false) return;

        const startDateItemData = startDateSelectedItem.data;
        const isStartDateItemDuration =
            isDataDurationTypeGuard(startDateItemData);

        if (isStartDateItemDuration) {
            setEndDateSelectedItem(newEndDateItem);
            onChange(
                transformRangeToOutputFormat({
                    startDate: calculateDateFromDuration({
                        referenceDate: newEndDateString,
                        formatString: "MM/yyyy",
                        duration: startDateItemData.value,
                    }),
                    endDate: newEndDateString,
                }),
            );
            return;
        }

        const currentStartDateString = getStartDateString(
            startDateSelectedItem,
            currentDate,
        );
        const currentStartDate = parseDateIfItsValidAndMatchesFormat({
            dateString: currentStartDateString,
            formatString: "MM/yyyy",
        });
        if (currentStartDate === false) return;

        if (isAfter(currentStartDate, newEndDate)) {
            onError("Start date cannot be after end date");
            forceReset();
            return;
        }

        setEndDateSelectedItem(newEndDateItem);
        onChange(
            transformRangeToOutputFormat({
                startDate: currentStartDateString,
                endDate: newEndDateString,
            }),
        );
    };

    const handleOutputYearChange = (
        newYearItem: TMonthsBasedDatePickerItem<
            | TMonthsBasedDatePickerDataDate
            | TMonthsBasedDatePickerDataManuallyTypedDate
        >,
    ) => {
        const data = newYearItem.data;
        if (!isDataDateTypeGuard(data) && !isDataManuallyTypedTypeGuard(data)) {
            return;
        }

        const parsedYearOrFalse = parseDateIfItsValidAndMatchesFormat({
            dateString: data.value,
            formatString: "yyyy",
        });
        if (parsedYearOrFalse === false) return;

        const yearString = format(parsedYearOrFalse, "yyyy");
        const selectedYear = Number(yearString);
        const currentYear = getYearFromMonthYearString(currentDate);
        const endDateString =
            selectedYear === currentYear
                ? currentDate
                : format(new Date(selectedYear, 11, 1), "MM/yyyy");

        const mappedEndDateItem: TEndDateItem = {
            data: { type: "date", value: endDateString },
            label: endDateString,
            id: endDateString,
        };
        handleEndDateChange(mappedEndDateItem);
    };

    const outputYearSelectedItem = React.useMemo(() => {
        const endDateString = getEndDateString(endDateSelectedItem);
        const year = getYearFromMonthYearString(endDateString);
        return {
            data: { type: "date", value: `${year}` },
            label: `${year}`,
            id: `${year}`,
        } satisfies TMonthsBasedDatePickerItem<TMonthsBasedDatePickerDataDate>;
    }, [endDateSelectedItem]);

    return (
        <Box
            sx={{ display: "flex", flexDirection: "row", gap: "8px", ...sx }}
            className={clsx([className, testIds.main])}
            {...otherProps}
        >
            <MonthsBasedDatePicker
                key={`start-date-${key}`}
                value={startDateSelectedItem}
                options={startDateOptions}
                onChange={handleStartDateChange}
                errorCallback={onError}
                label="No older than"
                disabled={disabled}
                sx={{ width: "130px" }}
                data-testid={testIds.startDate}
            />
            {showOutputYear && (
                <MonthsBasedDatePicker
                    key={`end-year-${key}`}
                    value={outputYearSelectedItem}
                    options={yearOptions}
                    onChange={handleOutputYearChange}
                    errorCallback={onError}
                    label="Output year"
                    formatString="yyyy"
                    disabled={disabled}
                    sx={{ width: "130px" }}
                    data-testid={testIds.endDate}
                />
            )}
            {!showOutputYear && showOutputMonth && (
                <MonthsBasedDatePicker
                    key={`end-date-${key}`}
                    value={endDateSelectedItem}
                    options={endDateOptions}
                    onChange={handleEndDateChange}
                    errorCallback={onError}
                    label="Output month"
                    disabled={disabled}
                    sx={{ width: "130px" }}
                    data-testid={testIds.endDate}
                />
            )}
        </Box>
    );
};

export default MonthBasedRangePicker;
